import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import "./hapi.css"

/* Vendored from the Studio Happens mascot kit (code/react/Hapi.tsx).
 *
 * Treat it as somebody else's file. The rules the kit states — the eyes never
 * wobble, the tail is the only limb, he never changes colour, `tangled` is the
 * only negative pose — are enforced by this file and nowhere else, so a change
 * made here is a change made to the mascot everywhere the studio uses him.
 * Everything specific to this platform lives beside it in HapiBuddy.tsx.
 *
 * Four changes were needed to compile under this app's TypeScript, which runs
 * with `noUncheckedIndexedAccess`. They are the whole patch, and re-vendoring
 * means re-applying exactly these:
 *   - the "use client" line, a Next.js directive, dropped
 *   - `at()` in loopPath asserted non-null (the index is taken modulo N)
 *   - the two `to[i]` reads in useGlide asserted (same length as `from`)
 *   - the IntersectionObserver entry asserted, and useGlide's result typed as
 *     the three-tuple it always is
 * Nothing it draws was touched. */

// Phones skip clock ticks while the page scrolls, so the frame budget goes to the scroll
let lastScroll = -Infinity
let listening = false
function isScrolling(quiet = 220) {
  if (!listening && typeof window !== "undefined") {
    listening = true
    window.addEventListener("scroll", () => (lastScroll = performance.now()), { passive: true, capture: true })
  }
  return performance.now() - lastScroll < quiet
}
const isTouch = () => typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches

/* ------------------------------------------------------------------ *
 * HAPI, the Studio Happens mascot, for the web.
 *
 * He IS the logo mark: the tangled marker scribble, alive. The loose
 * crimson tail is his one limb.
 *
 * Rules carried over from MASCOT.md:
 *   - Eyes never wobble or boil. They may LOOK (follow the cursor), that's all.
 *   - The tail is the only limb.
 *   - He never changes colour: ink + crimson, or white + crimson on dark.
 *   - `tangled` is the only negative pose, and the mess is the enemy.
 *   - `point` must line up with a real target.
 *
 * Web differences: time comes from a stepped clock (every 4 frames at
 * 30fps, the house stop-motion rate) that only runs while he's on screen.
 * Eye tracking runs on rAF so it stays smooth.
 * ------------------------------------------------------------------ */

export type HapiPose = "idle" | "tangled" | "think" | "point" | "happy" | "shock" | "roll" | "sleepy" | "push"

const CRIMSON = "#F72E63"
const INK = "#1A1420"
const PAPER = "#FCFAF6"
const FPS = 30
const STEP_MS = (1000 / FPS) * 4

/** Deterministic [0,1): same seed+step always gives the same number. */
const rnd = (seed: number, t: number): number => {
  const x = Math.sin(seed * 127.1 + t * 311.7) * 43758.5453
  return x - Math.floor(x)
}

export type Pt = { x: number; y: number }

/** A closed, hand-drawn loop: an ellipse whose radius wanders. */
const loopPath = (cx: number, cy: number, rx: number, ry: number, rotDeg: number, seed: number, wobble: number) => {
  const N = 32
  const rot = (rotDeg * Math.PI) / 180
  const pts: Pt[] = []
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2
    const k = 1 + (rnd(seed, i) - 0.5) * 2 * wobble
    const ex = Math.cos(a) * rx * k
    const ey = Math.sin(a) * ry * k
    pts.push({ x: cx + ex * Math.cos(rot) - ey * Math.sin(rot), y: cy + ex * Math.sin(rot) + ey * Math.cos(rot) })
  }
  const at = (i: number) => pts[(i + N * 2) % N]!
  let d = `M ${at(0).x.toFixed(2)} ${at(0).y.toFixed(2)}`
  for (let i = 0; i < N; i++) {
    const p0 = at(i - 1), p1 = at(i), p2 = at(i + 1), p3 = at(i + 2)
    const c1 = { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 }
    const c2 = { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 }
    d += ` C ${c1.x.toFixed(2)} ${c1.y.toFixed(2)}, ${c2.x.toFixed(2)} ${c2.y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`
  }
  return d + " Z"
}

const RINGS = (ink: string) => [
  { rx: 72, ry: 46, rot: -38, seed: 3, w: 0.05, c: CRIMSON, sw: 8, dx: 9, dy: 7 },
  { rx: 67, ry: 43, rot: 26, seed: 11, w: 0.06, c: CRIMSON, sw: 7, dx: 13, dy: 2 },
  { rx: 74, ry: 48, rot: -8, seed: 5, w: 0.05, c: ink, sw: 8, dx: 0, dy: 0 },
  { rx: 70, ry: 45, rot: 34, seed: 7, w: 0.06, c: ink, sw: 7, dx: -3, dy: 2 },
  { rx: 66, ry: 42, rot: 72, seed: 13, w: 0.06, c: ink, sw: 7, dx: 2, dy: -2 },
  { rx: 62, ry: 40, rot: -70, seed: 17, w: 0.07, c: ink, sw: 6, dx: -1, dy: 3 },
]

/** The scribble ball. `mess` 0..1 tightens or explodes the tangle. */
export function HapiScribble({ mess, step, ink, squash = 1, spin = 0 }: { mess: number; step: number; ink: string; squash?: number; spin?: number }) {
  const cx = 120
  const cy = 116
  return (
    <g transform={`translate(${cx} ${cy}) rotate(${spin}) scale(${1 / squash} ${squash}) translate(${-cx} ${-cy})`}>
      {RINGS(ink).map((r, i) => {
        const drift = (rnd(r.seed, step) - 0.5) * 2 * (1.5 + mess * 7)
        const rot = r.rot + drift * 0.9 + mess * (i % 2 ? 9 : -9)
        return (
          <path
            key={i}
            d={loopPath(cx + r.dx + drift, cy + r.dy + drift * 0.6, r.rx * (1 + mess * 0.06), r.ry * (1 + mess * 0.04), rot, r.seed, r.w + mess * 0.06)}
            fill="none"
            stroke={r.c}
            strokeWidth={r.sw}
            strokeLinecap="round"
          />
        )
      })}
    </g>
  )
}

export const HAPI_TAIL_START = { x: 158, y: 152 }
const TAIL_START = HAPI_TAIL_START
export const HAPI_TAIL_END: Record<HapiPose, Pt> = {
  idle: { x: 210, y: 196 },
  tangled: { x: 196, y: 214 },
  think: { x: 214, y: 186 },
  point: { x: 246, y: 74 },
  happy: { x: 226, y: 62 },
  shock: { x: 232, y: 208 },
  roll: { x: 206, y: 200 },
  sleepy: { x: 198, y: 210 },
  // Pushing with his one limb: the tail braced straight ahead
  push: { x: 252, y: 150 },
}

export type HapiHandle = { el: HTMLDivElement | null }

/**
 * Eases a few numbers to new targets over `ms`, so pose changes glide
 * (tail swings round, the tangle loosens) instead of snapping.
 */
function useGlide(target: number[], ms = 320): number[] {
  const [value, setValue] = useState(target)
  const current = useRef(target)
  const key = target.map((n) => n.toFixed(2)).join(",")
  useEffect(() => {
    const from = current.current
    const to = key.split(",").map(Number)
    if (from.every((n, i) => Math.abs(n - to[i]!) < 0.01)) return
    let raf = 0
    const t0 = performance.now()
    const tick = (now: number) => {
      const k = Math.min(1, (now - t0) / ms)
      const e = 1 - Math.pow(1 - k, 3)
      const next = from.map((n, i) => n + (to[i]! - n) * e)
      current.current = next
      setValue(next)
      if (k < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [key, ms])
  return value
}

type Props = {
  pose?: HapiPose
  /** Rendered width; he is square. Any CSS length. */
  size?: number | string
  colorway?: "light" | "dark"
  /** Override the tangle (0 calm .. 1 knotted), e.g. scrubbed by scroll. */
  mess?: number
  /** Eyes follow the pointer. */
  followCursor?: boolean
  flip?: boolean
  tilt?: number
  /** Overrides the pose's tail end, in the 240-unit viewBox. */
  tailTo?: Pt
  /** Body rotation in degrees while rolling, e.g. from distance travelled. */
  rollAngle?: number
  className?: string
  style?: React.CSSProperties
  label?: string
}

export const Hapi = forwardRef<HapiHandle, Props>(function Hapi(
  { pose = "idle", size = 240, colorway = "light", mess: messOverride, followCursor = true, flip = false, tilt = 0, tailTo, rollAngle, className, style, label = "Hapi, the Studio Happens mascot" },
  ref,
) {
  const wrap = useRef<HTMLDivElement>(null)
  const eyes = useRef<SVGGElement>(null)
  const [step, setStep] = useState(0)
  useImperativeHandle(ref, () => ({ el: wrap.current }), [])

  // Stepped clock, only while visible and motion is allowed
  useEffect(() => {
    const el = wrap.current
    if (!el) return
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduced) return
    const touch = isTouch()
    let timer: ReturnType<typeof setInterval> | undefined
    const start = () => {
      if (timer) return
      // Phones skip ticks while scrolling: each one repaints the drawing
      timer = setInterval(() => {
        if (touch && isScrolling()) return
        setStep((s) => s + 4)
      }, STEP_MS)
    }
    const stop = () => {
      if (timer) clearInterval(timer)
      timer = undefined
    }
    const io = new IntersectionObserver(([e]) => (e!.isIntersecting ? start() : stop()), { rootMargin: "100px" })
    io.observe(el)
    return () => {
      io.disconnect()
      stop()
    }
  }, [])

  // Eyes look toward the pointer (smooth, never wobbling)
  useEffect(() => {
    if (!followCursor) return
    const el = wrap.current
    const g = eyes.current
    if (!el || !g) return
    let tx = 0, ty = 0, x = 0, y = 0, raf = 0
    const onMove = (e: PointerEvent) => {
      const r = el.getBoundingClientRect()
      const cx = r.left + r.width * 0.5
      const cy = r.top + r.height * 0.46
      const dx = e.clientX - cx
      const dy = e.clientY - cy
      const dist = Math.hypot(dx, dy) || 1
      const reach = Math.min(1, dist / 260)
      tx = (dx / dist) * 5.5 * reach * (flip ? -1 : 1)
      ty = (dy / dist) * 4.5 * reach
      if (!raf) raf = requestAnimationFrame(loop)
    }
    const loop = () => {
      x += (tx - x) * 0.18
      y += (ty - y) * 0.18
      g.setAttribute("transform", `translate(${x.toFixed(2)} ${y.toFixed(2)})`)
      raf = Math.abs(tx - x) + Math.abs(ty - y) > 0.02 ? requestAnimationFrame(loop) : 0
    }
    window.addEventListener("pointermove", onMove, { passive: true })
    return () => {
      window.removeEventListener("pointermove", onMove)
      cancelAnimationFrame(raf)
    }
  }, [followCursor, flip])

  const ink = colorway === "dark" ? PAPER : INK
  const paper = colorway === "dark" ? INK : PAPER
  const t = step / FPS

  // The hop keeps going until he lands, so leaving "happy" never drops him mid-air
  const [hop, setHop] = useState(false)
  if (pose === "happy" && !hop) setHop(true)
  const onLand = () => {
    if (pose !== "happy") setHop(false)
  }

  const sleepy = pose === "sleepy"
  const breathe = Math.sin(t * (sleepy ? 1.1 : 2.2)) * 0.5 + 0.5
  const squash =
    pose === "happy"
      ? 1 + Math.sin(t * 7) * 0.05
      : pose === "push"
        ? 1.05 + Math.sin(t * 5) * 0.015
        : 1 + (breathe - 0.5) * (sleepy ? 0.07 : 0.03)
  const poseMess = pose === "tangled" ? 1 : pose === "shock" ? 0.45 : 0
  const roll = pose === "roll" ? (rollAngle ?? (step * 6) % 360) : 0
  const lean = pose === "think" ? -9 : pose === "point" ? 5 : pose === "push" ? 11 : sleepy ? 7 : 0
  const blink = pose === "happy" || step % 76 > 3 ? 1 : 0.12

  const aim = tailTo ?? HAPI_TAIL_END[pose]
  // The cast states what useGlide guarantees and TypeScript cannot see: it
  // returns one number per number it was given. Said once here rather than as
  // six assertions further down.
  const [endX, endY, glidedMess] = useGlide([aim.x, aim.y, poseMess]) as [number, number, number]
  const end = { x: endX, y: endY }
  // An external mess (scrubbed by scroll) is already smooth; pose mess glides
  const mess = messOverride ?? glidedMess
  const wag =
    pose === "think"
      ? Math.sin(t * 6) * 14
      : pose === "happy"
        ? Math.sin(t * 8) * 20
        : pose === "push"
          ? Math.sin(t * 5) * 3
          : Math.sin(t * (sleepy ? 0.8 : 1.6)) * 5
  const tail = useMemo(() => {
    const mx = (TAIL_START.x + end.x) / 2
    const my = (TAIL_START.y + end.y) / 2
    const nx = -(end.y - TAIL_START.y)
    const ny = end.x - TAIL_START.x
    const len = Math.max(1, Math.hypot(nx, ny))
    const c = { x: mx + (nx / len) * wag, y: my + (ny / len) * wag }
    return `M ${TAIL_START.x} ${TAIL_START.y} Q ${c.x.toFixed(1)} ${c.y.toFixed(1)}, ${end.x.toFixed(1)} ${end.y.toFixed(1)}`
  }, [end.x, end.y, wag])

  // Eyes
  const wide = pose === "shock"
  const arc = pose === "happy"
  const worried = pose === "tangled" || (messOverride ?? 0) > 0.6
  const erx = wide ? 13 : 10
  const ery = (wide ? 15 : 12) * blink
  const look = pose === "think" ? -4 : pose === "point" || pose === "push" ? 5 : 0
  const L = { x: 105 + look, y: 112 }
  const R = { x: 136 + look, y: 110 }

  return (
    <div
      ref={wrap}
      role="img"
      aria-label={label}
      className={className}
      style={{ width: size, aspectRatio: "1 / 1", ...style }}
    >
      <svg
        viewBox="0 0 240 240"
        width="100%"
        height="100%"
        className={hop ? "hapi-hop" : undefined}
        onAnimationIteration={onLand}
        style={{
          overflow: "visible",
          transform: `scale(${flip ? -1 : 1}, 1) rotate(${tilt + lean}deg)`,
          transformOrigin: "50% 62%",
          transition: "transform 200ms cubic-bezier(.2,.8,.2,1)",
        }}
      >
        <path d={tail} fill="none" stroke={CRIMSON} strokeWidth={8} strokeLinecap="round" />
        <HapiScribble mess={mess} step={step} ink={ink} squash={squash} spin={roll} />
        <g ref={eyes}>
          {sleepy ? (
            <g stroke={ink} strokeWidth={5} fill="none" strokeLinecap="round">
              <path d={`M ${L.x - 10} ${L.y + 1} Q ${L.x} ${L.y + 7}, ${L.x + 10} ${L.y + 1}`} />
              <path d={`M ${R.x - 10} ${R.y + 1} Q ${R.x} ${R.y + 7}, ${R.x + 10} ${R.y + 1}`} />
            </g>
          ) : arc ? (
            <g stroke={ink} strokeWidth={6} fill="none" strokeLinecap="round">
              <path d={`M ${L.x - 11} ${L.y + 4} Q ${L.x} ${L.y - 9}, ${L.x + 11} ${L.y + 4}`} />
              <path d={`M ${R.x - 11} ${R.y + 4} Q ${R.x} ${R.y - 9}, ${R.x + 11} ${R.y + 4}`} />
            </g>
          ) : (
            <g>
              <ellipse cx={L.x} cy={L.y} rx={erx} ry={ery} fill={ink} />
              <ellipse cx={R.x} cy={R.y} rx={erx} ry={ery} fill={ink} />
              {ery > 4 ? (
                <>
                  <circle cx={L.x + 3.2} cy={L.y - 3.4} r={3} fill={paper} />
                  <circle cx={R.x + 3.2} cy={R.y - 3.4} r={3} fill={paper} />
                </>
              ) : null}
              {worried ? (
                // Inner ends raised: overwhelmed, never angry (the mess is the enemy, not him)
                <g stroke={ink} strokeWidth={4.5} strokeLinecap="round" fill="none">
                  <path d={`M ${L.x - 12} ${L.y - 15} Q ${L.x - 2} ${L.y - 19}, ${L.x + 7} ${L.y - 23}`} />
                  <path d={`M ${R.x + 12} ${R.y - 15} Q ${R.x + 2} ${R.y - 19}, ${R.x - 7} ${R.y - 23}`} />
                </g>
              ) : null}
            </g>
          )}
        </g>
      </svg>
    </div>
  )
})

/** Static logo mark from the same construction (no face, no clock). */
export function HapiMark({ size = 28, colorway = "light", className }: { size?: number; colorway?: "light" | "dark"; className?: string }) {
  const ink = colorway === "dark" ? PAPER : INK
  return (
    <svg viewBox="30 40 200 200" width={size} height={size} className={className} aria-hidden="true" style={{ overflow: "visible" }}>
      <path d={`M ${TAIL_START.x} ${TAIL_START.y} Q 190 170, 210 196`} fill="none" stroke={CRIMSON} strokeWidth={10} strokeLinecap="round" />
      <HapiScribble mess={0} step={0} ink={ink} />
    </svg>
  )
}
